import { useCallback, useState } from 'react'
import { Variant } from '@phrasee/phrasee-typings/typings/futurama/element'
import cx from 'classnames'
import isEqual from 'lodash/isEqual'

import { successToast } from 'common/components/toastNotification'
import TableWidget from 'common/components/widget/tableWidget'
import { useAppSelector } from 'common/hooks/redux'
import useDeleteVariantMutation from 'features/unifiedFlow/api/mutations/useDeleteVariantMutation'
import useMoreLikeThisVariantsMutation from 'features/unifiedFlow/api/mutations/useMoreLikeThisVariantsMutation'
import useReplaceVariantMutation from 'features/unifiedFlow/api/mutations/useReplaceVariantMutation'
import useSelectVariantMutation from 'features/unifiedFlow/api/mutations/useSelectVariantMutation'
import useUpdateVariantMutation from 'features/unifiedFlow/api/mutations/useUpdateVariantMutation'
import {
  useSelectedComponentElement,
  useVariants,
} from 'features/unifiedFlow/contentPage/hooks'
import useWebsocketMessage from 'features/unifiedFlow/contentPage/hooks/websocketMessage'
import { getWsTopic } from 'features/unifiedFlow/contentPage/hooks/websocketMessage/helpers'

import { useVariantInsights } from '../../context/VariantInsightsContext'
import TableHeading from '../tableHeading'

import { useContentTableColumns } from './hooks/useContentTableColumns'
import { useCoreVariantsWebsocketCallbacks } from './hooks/useCoreVariantsWebsocketCallbacks'
import useCoreVariantsWebsocketSubscription from './hooks/useCoreVariantsWebsocketSubscription'
import useModifiedVariants from './hooks/useModifiedVariants'
import ContentTableActionButtons from './contentTableActionButtons'
import EditModal from './EditModal'
import useSelectedRows from './useSelectedRows'

import classes from './ContentTable.module.css'

export type ExtendedVariant = Variant & {
  isDisabled?: boolean
  checked?: boolean
  subRows?: ExtendedVariant[]
  isRefreshSuccessful?: boolean
  isRefreshing?: boolean
  isMoreLikeThisSuccessful?: boolean
  isLoadingMoreLikeThis?: boolean
}

type Props = {
  isReadOnly?: boolean
}

const ContentTable = ({ isReadOnly = false }: Props) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [selectedVariant, setSelectedVariant] = useState<Variant | undefined>(
    undefined
  )
  const { sendWebsocketMessage } = useWebsocketMessage()

  const { setVariantState } = useVariantInsights()

  const {
    refreshSuccessVariantIds,
    moreLikeThisSuccessVariantIds,
    onRefreshSuccess,
    onMoreLikeThisSuccess,
  } = useCoreVariantsWebsocketCallbacks()

  useCoreVariantsWebsocketSubscription({
    onRefreshSuccess,
    onMoreLikeThisSuccess,
  })

  const accountId = useAppSelector((state) => state.authStates.accountId)

  const activeContentId = useAppSelector(
    (state) => state.unifiedFlow.activeContentId
  )

  const { data: variants } = useVariants(activeContentId, { withSubrows: true })
  const { data: selectedElement } = useSelectedComponentElement()

  const elementId = selectedElement?.element_id

  const { selectedRows, setSelectedRows } = useSelectedRows(
    variants,
    selectedElement
  )

  const replaceVariantMutation = useReplaceVariantMutation()
  const moreLikeThisVariantsMutation = useMoreLikeThisVariantsMutation()
  const selectVariantMutation = useSelectVariantMutation(activeContentId)

  const variantsWithModifiedState = useModifiedVariants(
    variants,
    refreshSuccessVariantIds,
    moreLikeThisSuccessVariantIds
  )

  const isApproveButtonDisabled = variantsWithModifiedState.some((variant) => {
    if (variant.isRefreshing || variant.isLoadingMoreLikeThis) {
      return true
    }

    if (variant.subRows) {
      return variant.subRows.some((subRow) => subRow.isRefreshing)
    }

    return false
  })

  const handleReplaceVariants = useCallback(
    async (variantId: number) => {
      if (activeContentId && elementId) {
        const wsTopic = getWsTopic({
          data: {
            contentId: activeContentId,
            elementId,
            variantId: String(variantId),
          },
          action: 'refreshVariant',
        })

        sendWebsocketMessage({
          action: 'refreshVariant',
          data: {
            contentId: activeContentId,
            elementId,
            variantId: String(variantId),
          },
          subscriptionAction: 'subscribe',
        })

        replaceVariantMutation.mutate({
          accountId,
          contentId: activeContentId,
          elementId,
          variantId,
          wsTopic,
        })
      }
    },
    [
      accountId,
      activeContentId,
      elementId,
      replaceVariantMutation,
      sendWebsocketMessage,
    ]
  )

  const deleteVariantMutation = useDeleteVariantMutation(activeContentId)
  const updateVariantMutation = useUpdateVariantMutation(activeContentId)

  const handleDeleteVariants = useCallback(
    (variantToDelete: Variant) => {
      if (activeContentId && elementId) {
        deleteVariantMutation.mutate(
          {
            accountId,
            contentId: activeContentId,
            elementId: elementId,
            variantId: variantToDelete.variant_id,
          },
          {
            onSuccess: () => {
              successToast('Line deleted successfully')
            },
          }
        )
      }
    },
    [accountId, activeContentId, deleteVariantMutation, elementId]
  )

  const handleMoreLikeThisVariants = useCallback(
    (variantId: number) => {
      if (activeContentId && elementId) {
        const wsTopic = getWsTopic({
          data: {
            contentId: activeContentId,
            elementId,
            variantId: String(variantId),
          },
          action: 'moreLikeThisVariants',
        })

        sendWebsocketMessage({
          action: 'moreLikeThisVariants',
          data: {
            contentId: activeContentId,
            elementId,
            variantId: String(variantId),
          },
          subscriptionAction: 'subscribe',
        })

        moreLikeThisVariantsMutation.mutate({
          accountId,
          contentId: activeContentId,
          elementId,
          variantId,
          wsTopic,
        })
      }
    },
    [
      accountId,
      activeContentId,
      elementId,
      moreLikeThisVariantsMutation,
      sendWebsocketMessage,
    ]
  )
  const onEditClick = useCallback((variant: Variant) => {
    setIsEditModalOpen(true)
    setSelectedVariant(variant)
  }, [])

  const handleUpdateVariant = async (updatedVariantText: string) => {
    if (
      activeContentId &&
      elementId &&
      updatedVariantText &&
      selectedVariant?.variant_id
    ) {
      await updateVariantMutation.mutateAsync({
        accountId,
        contentId: activeContentId,
        elementId,
        variantId: selectedVariant?.variant_id,
        updatedVariantText,
      })
    }
  }

  const columns = useContentTableColumns({
    handleMoreLikeThisVariants,
    handleReplaceVariants,
    handleDeleteVariants,
    onEditClick,
  })

  const handleSelectRow = (rows: ExtendedVariant[]) => {
    const variantId = rows[0]?.variant_id

    const parsedRows: Variant[] = rows.map(
      ({ isDisabled, checked, ...row }) => row
    )
    if (
      !isEqual(parsedRows, selectedRows) &&
      activeContentId &&
      elementId &&
      variantId &&
      variantId !== selectedRows[0]?.variant_id
    ) {
      selectVariantMutation.mutate({
        accountId,
        contentId: activeContentId,
        elementId,
        variantId,
      })
      setSelectedRows(parsedRows)
    }
  }

  const handleRowClick = (row: ExtendedVariant) => {
    const variantId = row.variant_id

    const sentiments = row.sentiments

    if (sentiments) {
      const mappedSentiments = {
        appreciative:
          sentiments.appreciative &&
          sentiments.appreciative.score !== undefined &&
          sentiments.appreciative.score !== null
            ? {
                score: sentiments.appreciative.score,
                weights: sentiments.appreciative.weights || [],
              }
            : null,
        urgent:
          sentiments.urgent &&
          sentiments.urgent.score !== undefined &&
          sentiments.urgent.score !== null
            ? {
                score: sentiments.urgent.score,
                weights: sentiments.urgent.weights || [],
              }
            : null,
        excited:
          sentiments.excited &&
          sentiments.excited.score !== undefined &&
          sentiments.excited.score !== null
            ? {
                score: sentiments.excited.score,
                weights: sentiments.excited.weights || [],
              }
            : null,
        curious:
          sentiments.curious &&
          sentiments.curious.score !== undefined &&
          sentiments.curious.score !== null
            ? {
                score: sentiments.curious.score,
                weights: sentiments.curious.weights || [],
              }
            : null,
        helpful:
          sentiments.helpful &&
          sentiments.helpful.score !== undefined &&
          sentiments.helpful.score !== null
            ? {
                score: sentiments.helpful.score,
                weights: sentiments.helpful.weights || [],
              }
            : null,
        impressed:
          sentiments.impressed &&
          sentiments.impressed.score !== undefined &&
          sentiments.impressed.score !== null
            ? {
                score: sentiments.impressed.score,
                weights: sentiments.impressed.weights || [],
              }
            : null,
        surprising:
          sentiments.surprising &&
          sentiments.surprising.score !== undefined &&
          sentiments.surprising.score !== null
            ? {
                score: sentiments.surprising.score,
                weights: sentiments.surprising.weights || [],
              }
            : null,
      }

      setVariantState({
        variantId: String(variantId),
        sentiments: mappedSentiments,
      })
    }
  }

  if (variantsWithModifiedState.length === 0) {
    return null
  }

  return (
    <>
      <TableHeading
        heading={selectedElement?.display_name || selectedElement?.name}
        subHeading={selectedElement?.description}
      />
      <TableWidget.Widget<ExtendedVariant>
        className={cx('border-none bg-gold-40', classes.contentTable)}
        key={elementId}
        data={variantsWithModifiedState}
        columns={columns}
        initialState={{
          hiddenColumns: isReadOnly ? ['actions'] : [],
        }}
        autoResetPage={false}
        onSelectRow={handleSelectRow}
        rowSelection={isReadOnly ? undefined : 'single'}
        selectionHeader="Select a variant"
        onRowClick={handleRowClick}
        headerClassName="bg-gold-50"
        data-testid="content-variants-table"
      >
        <TableWidget.ActionButtons className="w-full">
          <ContentTableActionButtons
            isReadOnly={isReadOnly}
            isApproveButtonDisabled={isApproveButtonDisabled}
          />
        </TableWidget.ActionButtons>
      </TableWidget.Widget>
      <EditModal
        isOpen={isEditModalOpen}
        onEdit={handleUpdateVariant}
        onClose={() => {
          setSelectedVariant(undefined)
          setIsEditModalOpen(false)
        }}
        variantText={selectedVariant?.text || ''}
      />
    </>
  )
}

export default ContentTable
